/**
 * Lane 2 — live work (spec #44 §1.2).
 *
 * `head.active_agents` gives the tiles; each tile's one line is that
 * agent's NEWEST timeline title, or nothing at all when this screen has
 * not read that agent's thread yet. It never says "thinking…" or invents a
 * status: an agent with a cycle in flight and no title yet simply shows
 * the cycle, not a mood.
 *
 * Everyone else collapses into the prototype's single "Quiet right now"
 * chip row, and a tenant where nothing has EVER run gets the honest
 * "No cycle has run yet" plus the plan's own cadence.
 */

import { SkeletonBodyText, Text } from '@shopify/polaris';

import {
  liveAgentTiles,
  quietAgents,
  specialistTiles,
} from '../../contexts/workspaceComposition';
import { useWorkspace } from '../../hooks/useWorkspace';
import { isApprovedPlan } from '../../types/plan';
import type { TimelineEntry } from '../../types/timeline';
import { WorkspaceEmpty } from './WorkspaceEmpty';
import styles from './workspace.module.css';

export function LiveWorkLane() {
  const {
    agents,
    specialists,
    head,
    plan,
    loading,
    errors,
    entriesFor,
    openChannel,
  } = useWorkspace();

  const newestByDomain: Record<string, TimelineEntry | undefined> = {};
  agents.forEach((agent) => {
    newestByDomain[agent.domain] = entriesFor(agent.domain)[0];
  });

  const tiles = [
    ...liveAgentTiles(agents, head, newestByDomain),
    ...specialistTiles(specialists),
  ];
  const quiet = quietAgents(agents, head);
  // "Nothing has ever run here" is a different fact from "nothing is
  // running right now", and only the head can tell them apart.
  const neverRan =
    head !== null && Object.keys(head.latest_at ?? {}).length === 0;
  const cadence = plan && isApprovedPlan(plan) ? plan.cadence : null;

  return (
    <section className={styles.lane} aria-label="Live work">
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>Live work</span>
        <span className={styles.mono} data-testid="live-lane-count">
          {tiles.length}
        </span>
      </div>
      <div className={styles.laneCards}>
        {errors.rosters ? (
          <WorkspaceEmpty kind="lane-error" reason={errors.rosters} />
        ) : loading.rosters && agents.length === 0 ? (
          <SkeletonBodyText lines={3} />
        ) : agents.length === 0 ? (
          <WorkspaceEmpty kind="no-agents-configured" />
        ) : (
          <>
            {tiles.map((tile) => (
              <button
                key={tile.key}
                type="button"
                className={`${styles.tile} ${styles.tileWorking}`}
                data-testid={`live-tile-${tile.key}`}
                onClick={() => openChannel(tile.domain)}
              >
                <div>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {tile.label}
                  </Text>
                </div>
                {tile.detail ? (
                  <div className={styles.subtle}>{tile.detail}</div>
                ) : null}
              </button>
            ))}
            {tiles.length === 0 && neverRan ? (
              <WorkspaceEmpty
                kind="no-cycle"
                nextCadence={cadence ? `${cadence}` : null}
              />
            ) : null}
            {quiet.length > 0 ? (
              <div>
                <Text as="p" tone="subdued" variant="bodySm">
                  Quiet right now
                </Text>
                <div className={styles.quietRow}>
                  {quiet.map((agent) => (
                    <button
                      key={agent.domain}
                      type="button"
                      className={styles.chip}
                      data-testid={`quiet-chip-${agent.domain}`}
                      onClick={() => openChannel(agent.domain)}
                    >
                      {agent.display_name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {specialists.filter((s) => s.enabled).length === 0 ? (
              <WorkspaceEmpty kind="no-specialists" />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
